import { useCallback, useEffect, useMemo, useState } from "react";
import {
  MembershipChecklistResultFragmentFragment,
  MembershipCheckoutBookersDocument,
  MembershipLaunchStatusDocument,
  OrganizationByIdDocument,
  OrganizationsByUserDocument,
  RenewalRebaseGraceFragmentFragment,
  useBookerDuplicateToStorefrontMutation,
  useMembershipChecklistFixMutation,
  useMembershipChecklistLatestQuery,
  useMembershipChecklistRunMutation,
  useOrganizationUpdateMembershipTierCheckoutMutation,
} from "gql/generated";

export type ChecklistStatus = "done" | "notRelevant" | "notDone";

export type ChecklistResult = MembershipChecklistResultFragmentFragment;
export type ChecklistAnalysis = ChecklistResult["analysis"];

export type ChecklistFixAction =
  | "RESOLVE_TIERS"
  | "MARK_ACTIVE"
  | "FIX_STATUS"
  | "SET_DATES";

export type ChecklistFixPreview = {
  affected: number;
  date?: string | null;
  phaseAfter?: string | null;
  grace?: RenewalRebaseGraceFragmentFragment | null;
};

/** SET_DATES inputs beyond the date: the paid-for-cycle answer and grace choice. */
export type SetDatesOptions = {
  cyclePaid?: boolean;
  graceDays?: number;
  neverExpire?: boolean;
};

export type PaymentState =
  | "disabled"
  | "noStorefront"
  | "live"
  | "testOnly"
  | "incomplete";

export const isRenewalDateDriven = (renewal: ChecklistAnalysis["renewal"]) =>
  renewal.engineEnabled &&
  renewal.enabled &&
  renewal.term !== "MANUAL" &&
  renewal.term !== "LIFETIME";

export const getTiersStatus = (a: ChecklistAnalysis): ChecklistStatus => {
  if (a.members.byTier.length === 0) return "notDone";
  if (!a.memberListsEnabled) return "notRelevant";
  return a.members.byTier.every(tier => !!tier.listId) ? "done" : "notDone";
};

export const getTierConflictStatus = (a: ChecklistAnalysis): ChecklistStatus =>
  a.members.multipleTiers === 0 ? "done" : "notDone";

export const getRenewalsStatus = (a: ChecklistAnalysis): ChecklistStatus => {
  if (!isRenewalDateDriven(a.renewal)) return "notRelevant";
  if (
    a.renewal.renewalType === "FIXED_DATE" &&
    (!a.renewal.fixedRenewalMonth || !a.renewal.fixedRenewalDay)
  ) {
    return "notDone";
  }
  return a.renewal.withoutRenewalDate === 0 ? "done" : "notDone";
};

export const getStatusTagsStatus = (a: ChecklistAnalysis): ChecklistStatus => {
  if (a.members.byStatus.length === 0) return "notRelevant";
  return a.members.tieredWithoutStatusTag === 0 && a.renewal.tagDrift === 0
    ? "done"
    : "notDone";
};

export const getPaymentState = (a: ChecklistAnalysis): PaymentState => {
  if (!a.payment.enabled) return "disabled";
  if (!a.payment.hasStorefront) return "noStorefront";
  const tiers = a.payment.byTier;
  if (tiers.length === 0 || tiers.some(t => !t.free && !t.bookerId)) {
    return "incomplete";
  }
  return tiers.some(t => t.isTest) ? "testOnly" : "live";
};

export const getPaymentStatus = (a: ChecklistAnalysis): ChecklistStatus => {
  const state = getPaymentState(a);
  if (state === "disabled") return "notRelevant";
  return state === "live" ? "done" : "notDone";
};

export const getCommunicationStatus = (
  a: ChecklistAnalysis,
): ChecklistStatus =>
  a.communication.broadcastId &&
  a.communication.broadcastPublished &&
  a.communication.broadcastSharedToAll !== false
    ? "done"
    : "notDone";

/** Live storefront that a test-storefront widget should be copied into (same currency). */
export const getLiveTargetFor = (
  a: ChecklistAnalysis,
  testStorefrontId: string,
): string | null =>
  testStorefrontId === a.payment.testUsdStorefrontId
    ? (a.payment.liveUsdStorefrontId ?? null)
    : (a.payment.liveCadStorefrontId ?? null);

export const useMembershipChecklist = (organizationId?: string) => {
  const { data: latestData, loading: loadingLatest } =
    useMembershipChecklistLatestQuery({
      variables: { organizationId: organizationId ?? "" },
      skip: !organizationId,
      fetchPolicy: "cache-and-network",
    });
  const [result, setResult] = useState<ChecklistResult | null>(null);

  useEffect(() => {
    if (latestData?.membershipChecklistLatest) {
      setResult(latestData.membershipChecklistLatest);
    }
  }, [latestData]);

  const [runMutation, { loading: running }] =
    useMembershipChecklistRunMutation();
  const analyze = useCallback(async () => {
    if (!organizationId) return;
    const { data } = await runMutation({ variables: { organizationId } });
    if (data?.membershipChecklistRun) setResult(data.membershipChecklistRun);
  }, [organizationId, runMutation]);

  const [fixMutation] = useMembershipChecklistFixMutation();

  const previewFix = useCallback(
    async (
      action: ChecklistFixAction,
      date?: Date,
      setDates: SetDatesOptions = {},
    ): Promise<ChecklistFixPreview> => {
      const { data } = await fixMutation({
        variables: {
          organizationId: organizationId ?? "",
          action,
          dryRun: true,
          date: date?.toISOString() ?? null,
          ...setDates,
        },
      });
      const fix = data?.membershipChecklistFix;
      return {
        affected: fix?.affected ?? 0,
        date: fix?.date ?? null,
        phaseAfter: fix?.phaseAfter ?? null,
        grace: fix?.grace ?? null,
      };
    },
    [organizationId, fixMutation],
  );

  const applyFix = useCallback(
    async (
      action: ChecklistFixAction,
      date?: Date,
      statusTagId?: string,
      fromStatusTagId?: string | null,
      setDates: SetDatesOptions = {},
    ) => {
      const { data } = await fixMutation({
        variables: {
          organizationId: organizationId ?? "",
          action,
          dryRun: false,
          date: date?.toISOString() ?? null,
          statusTagId: statusTagId ?? null,
          fromStatusTagId: fromStatusTagId ?? null,
          ...setDates,
        },
        refetchQueries: [MembershipLaunchStatusDocument],
      });
      const fix = data?.membershipChecklistFix;
      if (fix?.result) setResult(fix.result);
      return fix?.affected ?? 0;
    },
    [organizationId, fixMutation],
  );

  const analysis = result?.analysis ?? null;

  const statuses = useMemo(
    () =>
      analysis
        ? {
            tiers: getTiersStatus(analysis),
            tierConflicts: getTierConflictStatus(analysis),
            renewals: getRenewalsStatus(analysis),
            statusTags: getStatusTagsStatus(analysis),
            payment: getPaymentStatus(analysis),
            communication: getCommunicationStatus(analysis),
          }
        : null,
    [analysis],
  );

  const [duplicateToStorefront] = useBookerDuplicateToStorefrontMutation();
  const [updateTierCheckout] =
    useOrganizationUpdateMembershipTierCheckoutMutation({
      refetchQueries: [
        OrganizationByIdDocument,
        OrganizationsByUserDocument,
        MembershipCheckoutBookersDocument,
      ],
    });
  const [copyingToLive, setCopyingToLive] = useState(false);

  /** Copies each test-only tier widget into the matching live storefront and re-points the tier. Returns how many widgets were copied. */
  const copyPaymentToLive = useCallback(async () => {
    if (!organizationId || !analysis) return 0;
    setCopyingToLive(true);
    try {
      const copiedByBooker = new Map<string, string>();
      for (const tier of analysis.payment.byTier) {
        if (!tier.isTest || !tier.bookerId || !tier.bookerStorefrontId) {
          continue;
        }
        const target = getLiveTargetFor(analysis, tier.bookerStorefrontId);
        if (!target) continue;

        let liveBookerId = copiedByBooker.get(tier.bookerId);
        if (!liveBookerId) {
          const copy = await duplicateToStorefront({
            variables: {
              bookerId: tier.bookerId,
              targetStorefrontId: target,
            },
          });
          liveBookerId = copy.data?.bookerDuplicateToStorefront?.bookerId;
          if (!liveBookerId) throw new Error("Copy returned no widget");
          copiedByBooker.set(tier.bookerId, String(liveBookerId));
        }

        await updateTierCheckout({
          variables: {
            organizationId,
            tierTagId: tier.tagId,
            bookerId: liveBookerId,
            free: null,
          },
        });
      }
      await analyze();
      return copiedByBooker.size;
    } finally {
      setCopyingToLive(false);
    }
  }, [organizationId, analysis, duplicateToStorefront, updateTierCheckout, analyze]);

  return {
    analyze,
    analyzing: running,
    loadingLatest: loadingLatest && !result,
    ranAt: result?.ranAt ?? null,
    analysis,
    statuses,
    previewFix,
    applyFix,
    copyPaymentToLive,
    copyingToLive,
  };
};
